import * as React from 'react';
import { Modal, PrimaryButton } from 'lib/components';
import styled from 'styled-components/macro';
import { MdDragHandle, MdAdd } from 'react-icons/md';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { theme } from 'lib/style';
import { ComponentProps } from 'lib/components/buttons/PrimaryButton';

import CloseIcon from '../../images/CloseIcon';

export interface Field {
  value: string;
  label: string;
}
type Props = {
  onSave: (selectedFields: Field[]) => void;
  onClose: () => void;
  allFields: Field[];
  initialFields: Field[];
  tableName: string;
  title: string;
  btnText?: string;
  saveToLocalStorage?: boolean;
  onBackClick?: () => void;
  maxFieldConfig?: any;
};

const Header = styled.div`
  display: flex;
  padding: 32px;
`;

const HeaderTitle = styled.div`
  ${theme.fontBold700}
  font-size: ${theme.fontSizes.lg};
  height: 24px;
  line-height: 1.33;
  letter-spacing: normal;
  color: ${theme.palette.black_1_100};
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  color: ${theme.palette.white};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Info = styled.div`
  width: 225px;
  height: 20px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.43;
  color: ${theme.palette.label};
`;

const AllFieldsWrap = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: start;
  width: 618px;
  height: 100%;
  margin-right: 24px;
  background-color: #eeeff2;
  padding: 10px;
`;

type FieldProps = {
  isSelected: boolean;
};

const FieldWrap = styled.div<FieldProps>`
  width: 250px;
  padding: 16px;
  margin: 12px 0 0 12px;
  border-radius: 5px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: ${props => (props.isSelected ? '0.3' : '1')};
  transition: all 0.1s;
`;

const Text = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: ${theme.palette.black_1_100};
`;

const IconWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #4e5461;
  color: white;
  cursor: pointer;
  font-size: 22px;
`;

const IconWrapX = styled(IconWrap)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e84c3d;
  color: white;
  cursor: pointer;
  transform: rotate(45deg);
  font-size: 22px;
`;

const SelectedFieldsWrap = styled.div`
  overflow-y: scroll;
  position: relative;
  height: 330px;
  padding: 24px;
  border: dashed 1px #d0d3d9;
  background-color: white;
`;

const SelectedFieldWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 16px;
  border-radius: 5px;
  background-color: #f6f7f9;
  &:last-child {
    margin-bottom: 0px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DragHandleWrap = styled.div`
  display: flex;
  align-items: center;
`;

const Body = styled.div`
  padding: 0 32px;
  display: flex;
  min-width: 1000px;
  min-height: 400px;
`;

const Footer = styled.div`
  padding: 32px;
`;

const StyledButton = styled(PrimaryButton)`
  height: 40px;
  font-size: ${theme.fontSizes.m};
  font-weight: 600;
  border-radius: 5px;
`;

const Button = (props: ComponentProps) => {
  return (
    <StyledButton
      iconPadding={8}
      textColor={theme.palette.black_1_100}
      textPadding='8px 16px 8px 0'
      margin='0 0 0 12px'
      center={true}
      {...props}
    />
  );
};

export const TABLE_NAME_PREFIX = 'CovideoTableFields';
export const ModalCustomTableFields = ({
  onSave,
  onClose,
  allFields,
  initialFields,
  tableName,
  title,
  btnText,
  saveToLocalStorage = true,
  onBackClick,
  maxFieldConfig = { count: 100, hideText: true },
}: Props) => {
  const [selectedFields, setSelectedFields] = React.useState<Field[]>([]);

  React.useEffect(() => {
    if (saveToLocalStorage) {
      const fields = localStorage.getItem(TABLE_NAME_PREFIX + tableName);
      if (fields) {
        setSelectedFields(JSON.parse(fields));
      } else {
        setSelectedFields(initialFields);
      }
    } else {
      setSelectedFields(initialFields);
    }
  }, [saveToLocalStorage]);

  const onSaveClick = () => {
    if (saveToLocalStorage) {
      localStorage.setItem(
        TABLE_NAME_PREFIX + tableName,
        JSON.stringify(selectedFields)
      );
    }
    onSave(selectedFields);
    onClose();
  };

  const isSelected = (field: Field): boolean => {
    return !!selectedFields.find(item => {
      return item.value === field.value;
    });
  };

  const reachMaxField = (): boolean => {
    return maxFieldConfig && selectedFields.length >= maxFieldConfig.count;
  };

  const onAddClick = (field: Field) => {
    if (
      !isSelected(field) &&
      maxFieldConfig &&
      selectedFields.length < maxFieldConfig.count
    ) {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const onRemoveClick = (field: Field) => {
    setSelectedFields(
      selectedFields.filter(item => item.value !== field.value)
    );
  };
  const reorder = (list: Field[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      selectedFields,
      result.source.index,
      result.destination.index
    );
    setSelectedFields(items);
  };

  return (
    <Modal style={{ position: 'fixed' }}>
      <Header>
        <HeaderTitle>{title}</HeaderTitle>
        <CloseButtonWrap title={'Close'}>
          <CloseIcon
            onClick={onClose}
            width={24}
            height={24}
            color={theme.palette.label}
          />
        </CloseButtonWrap>
      </Header>
      <Body>
        <Row>
          <Col>
            <Info>Select Data to show</Info>
            <AllFieldsWrap>
              {allFields.map(field => (
                <FieldWrap
                  key={field.value}
                  isSelected={isSelected(field) || reachMaxField()}
                >
                  <Text>{field.label}</Text>
                  <IconWrap onClick={() => onAddClick(field)}>
                    <MdAdd />
                  </IconWrap>
                </FieldWrap>
              ))}
            </AllFieldsWrap>
          </Col>
          <Col>
            <Info>
              Selected Data{' '}
              {!maxFieldConfig.hideText
                ? `(max ${maxFieldConfig.count})`
                : null}
            </Info>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId='selectedFields'>
                {provided => (
                  <SelectedFieldsWrap
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {selectedFields.map((field, index) => (
                      <Draggable
                        key={field.value}
                        draggableId={field.value}
                        index={index}
                      >
                        {provided => (
                          <SelectedFieldWrap
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <DragHandleWrap>
                              <MdDragHandle style={{ marginRight: 16 }} />
                              <Text>{field.label}</Text>
                            </DragHandleWrap>
                            {field.value !== 'email' && (
                              <IconWrapX onClick={() => onRemoveClick(field)}>
                                <MdAdd />
                              </IconWrapX>
                            )}
                          </SelectedFieldWrap>
                        )}
                      </Draggable>
                    ))}
                  </SelectedFieldsWrap>
                )}
              </Droppable>
            </DragDropContext>
          </Col>
        </Row>
      </Body>
      <Footer>
        <Row style={{ justifyContent: 'flex-end' }}>
          {onBackClick && (
            <Button
              onClick={onBackClick}
              text='Back'
              backgroundColor={theme.palette.primaryDarkBlue10Percent}
              textColor={theme.palette.black_1_100}
              textPadding='8px 16px 8px 16px'
            ></Button>
          )}

          <Button
            onClick={onSaveClick}
            text={btnText || 'Update'}
            textColor='#fff'
            textPadding='8px 16px 8px 16px'
          ></Button>
        </Row>
      </Footer>
    </Modal>
  );
};
